import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Typography, Card, Divider, Row, Col, Select, DatePicker, Button, message, Space } from 'antd';
import './Accounting.css';
import { useReactToPrint } from 'react-to-print';
import { QRCodeCanvas } from 'qrcode.react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import moment from 'moment';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const { Title, Text } = Typography;
const { Option } = Select;

const branchAddresses = {
  1: {
    name: "Keaes Academics (Ekamai)",
    address: `982/22 Gateway Ekamai, 4th Fl. Sukhumvit Rd,
Phra Khanong, Khlong Toei, Bangkok 10110`,
    tel: "Tel: 02-108-2922",
  },
  2: {
    name: "Keaes Academics (Ramkhamhaeng)",
    address: `61 Marketplace, 3rd Fl., Ramkhamhaeng Rd,
Saphan Sung, Bangkok 10240`,
    tel: "Tel: 02-729-5826",
  }
};


const formatNumber = (number, locale) => {
  const parsedNumber = parseFloat(number);
  if (isNaN(parsedNumber)) {
    return '0.00';
  }
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(parsedNumber);
};

const formatDate = (dateString, locale) => {
  const date = new Date(dateString);
  return moment(date).format('DD MMM YYYY');
};

const InvoiceModal = ({ visible, onClose, registration }) => {
  const { t, i18n } = useTranslation();
  const componentRef = useRef();
  const [bankDetails, setBankDetails] = useState([]);
  const [selectedBankId, setSelectedBankId] = useState(null);
  const [expirationDate, setExpirationDate] = useState(null);
  const [isEditing, setIsEditing] = useState(true);

   const API_URL = process.env.REACT_APP_API_URL;

  // Fetch bank details from API
  const fetchBankDetails = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/bank-details`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setBankDetails(response.data);
      
      // Set default bank if available
      if (response.data.length > 0) {
        const defaultBank = response.data.find(bank => bank.is_default) || response.data[0];
        setSelectedBankId(defaultBank.id);
      }
    } catch (error) {
      console.error('[fetchBankDetails] Error fetching bank details:', error);
      message.error('Failed to fetch bank details');
    }
  }, [API_URL]);

  // Initialize component data
  useEffect(() => {
    if (visible && registration) {
      // Fetch bank details when modal opens
      fetchBankDetails();
      
      // Set default expiration date (3 days from invoice date)
      const invoiceDate = moment(registration.date);
      const defaultExpiration = registration.expiration_date 
        ? moment(registration.expiration_date)
        : invoiceDate.clone().add(3, 'days');
      setExpirationDate(defaultExpiration);
      
      // Set selected bank if provided in registration
      if (registration.bank_id) {
        setSelectedBankId(registration.bank_id);
      }
    }
  }, [visible, registration, fetchBankDetails]);

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    onBeforeGetContent: () => {

    }
  });

  const handleFinishEditing = () => {
    if (!expirationDate) {
      message.error('Please select an expiration date');
      return;
    }
    if (!selectedBankId) {
      message.error('Please select a bank account');
      return;
    }
    setIsEditing(false);
  };

  const handleEditAgain = () => {
    setIsEditing(true);
  };

  if (!visible || !registration) return null;

  // Generate timestamp for invoice number if not provided
  const timestamp = moment().format('YYYYMMDDHHmm');
  const invoiceNo = registration.invoiceno || `INV-${timestamp}`;

  const branchInfo = branchAddresses[registration.branch_id] || {
  name: "Keaes Academics",
  address: "Default Address",
  tel: "Tel: -"
};

  // Get selected bank details
  const selectedBank = bankDetails.find(bank => bank.id === selectedBankId) || {};
  
  // Bank information with selected bank or fallback defaults
  const bankInfo = {
    bankName: selectedBank.bank_name || '',
    accountName: selectedBank.account_name || '',
    accountNumber: selectedBank.account_number || '',
    branchName: selectedBank.branch_name || '',
    swiftCode: selectedBank.swift_code || ''
  };

  // Format expiration date
  const formattedExpirationDate = expirationDate ? expirationDate.format('DD MMM YYYY') : '';

  // Calculate totals using new API structure
// Calculate totals from all class items
const classes = registration.classes || [];

const totalBeforeDiscount = classes.reduce(
  (sum, item) => sum + parseFloat(item.price || 0),
  0
);

const discountAmount = parseFloat(registration.discount || 0);
const totalAfterDiscount = totalBeforeDiscount - discountAmount;
const vatAmount = parseFloat(registration.vat || 0);

// Use batch_total_price if available, otherwise compute from after discount + VAT
const grandTotal = parseFloat(registration.batch_total_price || 0) 
  || (totalAfterDiscount + vatAmount);

const remainingBalance = parseFloat(registration.remaining_balance || 0);

  const isMultipleClasses = classes.length > 1;

  const imgToDataURL = (imgEl) => {
    try {
      if (!imgEl || !imgEl.complete || !imgEl.naturalWidth) return null;
      const canvas = document.createElement('canvas');
      canvas.width = imgEl.naturalWidth;
      canvas.height = imgEl.naturalHeight;
      canvas.getContext('2d').drawImage(imgEl, 0, 0);
      return canvas.toDataURL('image/png');
    } catch (e) {
      return null;
    }
  };

  const handleDownloadPDF = () => {
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageW = pdf.internal.pageSize.getWidth();
    const margin = 15;
    let y = 15;

    // Logo (top-left)
    const logoEl = componentRef.current?.querySelector('img[src*="keaes"]');
    const logoData = imgToDataURL(logoEl);
    const logoSize = 22;
    const textLeft = logoData ? margin + logoSize + 4 : margin;
    if (logoData) {
      pdf.addImage(logoData, 'PNG', margin, y, logoSize, logoSize);
    }

    // Branch info (to right of logo)
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(12);
    pdf.setTextColor(51, 51, 51);
    pdf.text(branchInfo.name, textLeft, y);
    y += 5;
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);
    pdf.setTextColor(74, 74, 74);
    (branchInfo.address || '').split('\n').forEach((line) => { pdf.text(line, textLeft, y); y += 4; });
    pdf.text('TAX ID 0000000000000', textLeft, y); y += 4;
    pdf.text(branchInfo.tel || '', textLeft, y); y += 4;
    pdf.text('Email: contact@keaes.com', textLeft, y); y += 4;
    y = Math.max(y, 15 + logoSize);

    // Invoice box (right)
    const boxW = 65;
    const boxH = 28;
    const boxX = pageW - margin - boxW;
    const boxY = 15;
    pdf.setDrawColor(180);
    pdf.setFillColor(230, 230, 230);
    pdf.rect(boxX, boxY, boxW, boxH, 'FD');
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(13);
    pdf.setTextColor(51, 51, 51);
    const title = registration.is_refunded ? 'CREDIT NOTE' : 'INVOICE';
    pdf.text(title, boxX + boxW / 2, boxY + 6, { align: 'center' });
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'normal');
    pdf.text(`No.: ${invoiceNo}`, boxX + 3, boxY + 13);
    pdf.text(`Date: ${formatDate(registration.date, i18n.language)}`, boxX + 3, boxY + 18);
    pdf.setFont('helvetica', 'bold');
    pdf.text(`Due Date: ${formattedExpirationDate}`, boxX + 3, boxY + 24);

    y = Math.max(y, boxY + boxH) + 5;

    // Customer info
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(10);
    pdf.setTextColor(51, 51, 51);
    pdf.text('Customer Information', margin, y);
    y += 4;
    const custBoxH = 18;
    pdf.setDrawColor(200);
    pdf.setFillColor(249, 249, 249);
    pdf.rect(margin, y, pageW - 2 * margin, custBoxH, 'FD');
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);
    pdf.text(`Name: ${registration.student_name || 'N/A'}`, margin + 3, y + 5);
    pdf.text(`Address: ${registration.address || 'N/A'}`, margin + 3, y + 10);
    pdf.text(`Phone: ${registration.phone || 'N/A'}`, margin + 3, y + 15);
    y += custBoxH + 5;

    // Items table
    const body = classes.map((item, i) => [
      String(i + 1),
      item.course_hours
        ? `${item.class_code}  (${(item.course_hours / 60).toFixed(1)}hrs)`
        : (item.class_code || 'Class'),
      '1',
      `THB ${formatNumber(item.price, i18n.language)}`,
      `THB ${formatNumber(item.price, i18n.language)}`,
    ]);
    autoTable(pdf, {
      startY: y,
      head: [['#', 'Description', 'Qty', 'Unit Price', 'Total']],
      body,
      margin: { left: margin, right: margin },
      styles: { fontSize: 9, cellPadding: 2, textColor: [51, 51, 51] },
      headStyles: { fillColor: [200, 200, 200], textColor: [51, 51, 51], fontStyle: 'bold' },
      columnStyles: {
        0: { halign: 'center', cellWidth: 10 },
        1: { cellWidth: 'auto' },
        2: { halign: 'right', cellWidth: 15 },
        3: { halign: 'right', cellWidth: 35 },
        4: { halign: 'right', cellWidth: 35, fontStyle: 'bold' },
      },
    });
    y = pdf.lastAutoTable.finalY + 5;

    // Summary box (right aligned)
    const sumRows = [
      ['Subtotal', `THB ${formatNumber(totalBeforeDiscount, i18n.language)}`, false],
    ];
    if (discountAmount > 0) sumRows.push(['Discount', `- THB ${formatNumber(discountAmount, i18n.language)}`, false]);
    sumRows.push(['Total After Discount', `THB ${formatNumber(totalAfterDiscount, i18n.language)}`, false]);
    if (vatAmount > 0) sumRows.push(['VAT 7%', `THB ${formatNumber(vatAmount, i18n.language)}`, false]);
    sumRows.push(['Grand Total', `THB ${formatNumber(grandTotal, i18n.language)}`, true]);
    if (remainingBalance > 0) sumRows.push(['Remaining Balance', `(THB ${formatNumber(remainingBalance, i18n.language)})`, false]);

    const sumBoxW = 90;
    const sumBoxX = pageW - margin - sumBoxW;
    const lineH = 5;
    const sumBoxH = sumRows.length * lineH + 4;
    pdf.setDrawColor(108, 117, 125);
    pdf.setFillColor(249, 249, 249);
    pdf.rect(sumBoxX, y, sumBoxW, sumBoxH, 'FD');
    pdf.setFontSize(9);
    let sy = y + 5;
    sumRows.forEach(([label, value, bold]) => {
      pdf.setFont('helvetica', bold ? 'bold' : 'normal');
      pdf.setTextColor(51, 51, 51);
      pdf.text(`${label}:`, sumBoxX + 3, sy);
      pdf.text(String(value), sumBoxX + sumBoxW - 3, sy, { align: 'right' });
      sy += lineH;
    });
    y += sumBoxH + 6;

    // Payment Information & Terms side by side
    const halfW = (pageW - 2 * margin - 5) / 2;
    const payY = y;

    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(10);
    pdf.setTextColor(51, 51, 51);
    pdf.text('Payment Information', margin, payY);
    pdf.text('Terms and Conditions', margin + halfW + 5, payY);

    const infoTop = payY + 3;
    const payLines = [
      bankInfo.bankName,
      bankInfo.accountName,
      `Account No. ${bankInfo.accountNumber}`,
    ];
    if (bankInfo.branchName) payLines.push(`Branch: ${bankInfo.branchName}`);
    if (bankInfo.swiftCode) payLines.push(`SWIFT: ${bankInfo.swiftCode}`);

    const terms = `This invoice is valid until ${formattedExpirationDate}. Kindly make the full tuition payment via bank transfer no later than ${formattedExpirationDate}. All payment cannot be refunded.`;
    const termsLines = pdf.splitTextToSize(terms, halfW - 6);

    const infoBoxH = Math.max(payLines.length * 5 + 6, termsLines.length * 4 + 6);
    pdf.setDrawColor(200);
    pdf.setFillColor(249, 249, 249);
    pdf.rect(margin, infoTop, halfW, infoBoxH, 'FD');
    pdf.rect(margin + halfW + 5, infoTop, halfW, infoBoxH, 'FD');

    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);
    let ly = infoTop + 5;
    payLines.forEach((line) => { pdf.text(line, margin + 3, ly); ly += 5; });

    pdf.setFontSize(8);
    let ty = infoTop + 5;
    termsLines.forEach((line) => { pdf.text(line, margin + halfW + 8, ty); ty += 4; });

    y = infoTop + infoBoxH + 8;

    // QR + Seal
    let qrDataURL = null;
    try {
      const qrCanvas = componentRef.current?.querySelector('canvas');
      if (qrCanvas) qrDataURL = qrCanvas.toDataURL('image/png');
    } catch (e) { /* ignore */ }

    const footerY = y;
    if (qrDataURL) {
      pdf.addImage(qrDataURL, 'PNG', margin, footerY, 20, 20);
      pdf.setFontSize(7);
      pdf.setFont('helvetica', 'bold');
      pdf.text(invoiceNo, margin + 10, footerY + 23, { align: 'center' });
    }

    pdf.setDrawColor(108, 117, 125);
    pdf.line(pageW - margin - 45, footerY + 18, pageW - margin, footerY + 18);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);
    pdf.text('Company Seal', pageW - margin - 22.5, footerY + 22, { align: 'center' });

    pdf.save(`invoice_${registration?.invoice_no || invoiceNo || 'invoice'}.pdf`);
  };

  return (
    <div className="invoice-modal-overlay">
      <div className="invoice-modal" ref={componentRef}>
        <span className="modal-close-button" onClick={onClose}>&times;</span>
        
        {/* Settings Panel - Only show when editing */}
        {isEditing && (
          <Card 
            title="Invoice Settings" 
            size="small" 
            style={{ 
              marginBottom: 20,
              backgroundColor: '#f6ffed',
              border: '1px solid #b7eb8f'
            }}
          >
            <Row gutter={16}>
              <Col span={12}>
                <Text strong style={{ color: '#333' }}>Expiration Date:</Text>
                <DatePicker 
                  value={expirationDate}
                  onChange={setExpirationDate}
                  format="DD MMM YYYY"
                  style={{ 
                    width: '100%', 
                    marginTop: 4,
                    borderColor: '#6c757d'
                  }}
                  placeholder="Select due date"
                />
              </Col>
              <Col span={12}>
                <Text strong style={{ color: '#333' }}>Bank Account:</Text>
                <Select
                  value={selectedBankId}
                  onChange={setSelectedBankId}
                  style={{ 
                    width: '100%', 
                    marginTop: 4
                  }}
                  placeholder="Select bank account"
                  loading={bankDetails.length === 0}
                >
                  {bankDetails.map(bank => (
                    <Option key={bank.id} value={bank.id}>
                      {bank.bank_name} - {bank.account_number}
                      {bank.is_default && <Text style={{ color: '#6c757d' }}> (Default)</Text>}
                    </Option>
                  ))}
                </Select>
              </Col>
            </Row>
            <Row style={{ marginTop: 16 }}>
              <Col span={24}>
                <Space>
                  <Button 
                    type="primary" 
                    onClick={handleFinishEditing}
                    style={{
                      backgroundColor: '#6c757d',
                      borderColor: '#6c757d',
                    }}
                  >
                    Generate Invoice
                  </Button>
                  <Button 
                    onClick={onClose}
                    style={{
                      borderColor: '#6c757d',
                      color: '#6c757d'
                    }}
                  >
                    Cancel
                  </Button>
                </Space>
              </Col>
            </Row>
          </Card>
        )}

        {/* Invoice Content - Only show when not editing */}
        {!isEditing && (
          <>
            
            {/* Edit Button - Top Left */}
            <Button 
              size="small" 
              onClick={handleEditAgain}
              style={{ 
                position: 'absolute', 
                top: 10, 
                left: 50, 
                zIndex: 10,
                backgroundColor: '#6c757d',
                borderColor: '#6c757d',
                color: 'white'
              }}
            >
              Edit Settings
            </Button>
            
            {/* Header section with logo and company info - Compact */}
           <Row justify="space-between" align="top" style={{ marginBottom: 8 }}>
              <Col span={14}>
               <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <img
                  src="/images/keaes150.png"
                  alt="KEAES Logo"
                  style={{ width: 100, height: 'auto' }}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
                <div>
                  <Title level={4} style={{ 
                    margin: '0 0 4px 0', 
                    fontWeight: 'bold', 
                    fontSize: '13px',
                    color: '#333'
                  }}>
                    {branchInfo.name}
                  </Title>
                  <Text style={{ fontSize: 9, display: 'block', lineHeight: '1.3', color: '#4a4a4a' }}>
                    {branchInfo.address.split("\n").map((line, i) => (
                      <React.Fragment key={i}>{line}<br /></React.Fragment>
                    ))}
                    TAX ID 0000000000000<br />
                    {branchInfo.tel}<br />
                    Email: contact@keaes.com
                  </Text>

                </div>
              </div>
            </Col>
              {/* Right side - Invoice box */}
              <Col span={10}>
              <Card
                size="small"
                style={{
                  background: 'rgba(200, 200, 200, 0.3)', 
                  textAlign: 'center',
                  marginLeft: 'auto',
                  maxWidth: 180,
                }}
              >
                <Title level={4} className="invoice-title" style={{ 
                  marginBottom: 4,
                  color: '#333',
                  fontSize: '16px'
                }}>
                  {registration.is_refunded ? t('creditNote').toUpperCase() : t('invoice').toUpperCase()}
                </Title>
                <div className="invoice-details" style={{ marginBottom: 4 }}>
                  <div className="detail-row" style={{ marginBottom: 2 }}>
                    <Text strong style={{ color: '#333', fontSize: '10px' }}>{t('No.')}:</Text>
                    <Text style={{ color: '#333', marginLeft: 6, fontSize: '10px' }}>{invoiceNo}</Text>
                  </div>
                  <div className="detail-row" style={{ marginBottom: 2 }}>
                    <Text strong style={{ color: '#333', fontSize: '10px' }}>{t('date')}:</Text>
                    <Text style={{ color: '#333', marginLeft: 6, fontSize: '10px' }}>{formatDate(registration.date, i18n.language)}</Text>
                  </div>
                  <div className="detail-row" style={{ marginBottom: 2 }}>
                    <Text strong style={{ color: '#333', fontSize: '10px' }}>{t('Due Date')}:</Text>
                    <Text style={{ 
                      color: '#333', 
                      marginLeft: 6,
                      fontWeight: 'bold',
                      fontSize: '10px'
                    }}>{formattedExpirationDate}</Text>
                  </div>
                </div>
              </Card>
              </Col>
            </Row>
            
            {/* Customer Information - Compact */}
            <div className="customer-section">
              <Title level={5} className="section-title" style={{ color: '#333', marginBottom: 6, fontSize: '13px' }}>
                {t('Customer Information')}
              </Title>
              <Card 
                size="small" 
                className="customer-card"
                style={{
                  backgroundColor: '#f9f9f9', // Very light green
                  border: '1px solid #ccc',
                  borderRadius: '6px',
                  marginBottom: 8
                }}
                bodyStyle={{ padding: '8px 12px' }}
              >
                <div className="customer-details">
                  <Text style={{ fontSize: '11px' }}><strong style={{ color: '#333' }}>{t('name')}:</strong> {registration.student_name}</Text><br />
                  <Text style={{ fontSize: '11px' }}><strong style={{ color: '#333' }}>{t('address')}:</strong> {registration.address || t('na')}</Text><br />
                  <Text style={{ fontSize: '11px' }}><strong style={{ color: '#333' }}>{t('phone')}:</strong> {registration.phone || t('na')}</Text>
                </div>
              </Card>
            </div>
            
            {/* Invoice Items as Rows - Compact Version */}
            <div className="table-section">
              <Title level={5} style={{ marginBottom: 8, color: '#333', fontSize: '14px' }}>
                {t('Invoice Items')}
              </Title>
             <Card 
                size="small" 
                style={{ 
                  marginBottom: 12,
                  border: '1px solid #ccc',
                  borderRadius: '6px'
                }}
              >
                {/* Header Row */}
                <div style={{
                  background: 'rgba(200, 200, 200, 0.3)', 
                  padding: '4px 8px',
                  borderBottom: '1px solid #495057',
                  fontSize: 11,
                  fontWeight: 'bold',
                  color: '#333',
                  borderRadius: '4px 4px 0 0',
                }}>
                  <Row>
                    <Col span={2} style={{ textAlign: 'center' }}>#</Col>
                    <Col span={10}>{t('description')}</Col>
                    <Col span={4} style={{ textAlign: 'right' }}>{t('quantity')}</Col>
                    <Col span={4} style={{ textAlign: 'right' }}>{t('unitPrice')}</Col>
                    <Col span={4} style={{ textAlign: 'right' }}>{t('total')}</Col>
                  </Row>
                </div>

                {/* Class Items */}
                {classes.map((classItem, index) => (
                  <div 
                    key={index}
                    style={{
                      padding: '4px 8px',
                      borderBottom: '1px solid #f0f0f0',
                      backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9f9f9', // Alternating green tint
                      fontSize: 11,
                      minHeight: '28px',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    <Row align="middle" style={{ width: '100%' }}>
                      <Col span={2} style={{ textAlign: 'center' }}>
                        <Text style={{ color: '#333', fontWeight: 'bold', fontSize: '11px' }}>{index + 1}</Text>
                      </Col>
                      <Col span={10}>
                        <Text style={{ color: '#333', fontWeight: '500', fontSize: '11px' }}>
                          {classItem.class_code}
                          {classItem.course_hours && (
                            <span style={{ fontSize: 9, color: '#495057', marginLeft: 4 }}>
                              ({(classItem.course_hours / 60).toFixed(1)}hrs)
                            </span>
                          )}
                        </Text>
                      </Col>
                      <Col span={4} style={{ textAlign: 'right' }}>
                        <Text style={{ color: '#333', fontSize: '11px' }}>1</Text>
                      </Col>
                      <Col span={4} style={{ textAlign: 'right' }}>
                        <Text style={{ color: '#333', fontSize: '11px' }}>฿{formatNumber(classItem.price, i18n.language)}</Text>
                      </Col>
                      <Col span={4} style={{ textAlign: 'right' }}>
                        <Text style={{ color: '#333', fontWeight: 'bold', fontSize: '11px' }}>฿{formatNumber(classItem.price, i18n.language)}</Text>
                      </Col>
                    </Row>
                  </div>
                ))}
                
              </Card>
            </div>
            
            {/* Totals Summary - Compact Version */}
            <Row style={{ marginBottom: 8 }}>
              <Col span={24}>
                <div style={{ 
                  fontSize: 11,
                  border: '1px solid #6c757d',
                  padding: 8,
                  background: 'linear-gradient(135deg, #f9f9f9 0%, #f0f0f0 100%)', // Light green gradient
                  borderRadius: 6,
                  width: 280,
                  marginLeft: 'auto',
                }}>
                  <Row justify="space-between" style={{ marginBottom: 2 }}>
                    <Text style={{ color: '#333', fontSize: '11px' }}>{t('subtotal')}:</Text>
                    <Text style={{ color: '#333', fontWeight: '500', fontSize: '11px' }}>฿{formatNumber(totalBeforeDiscount, i18n.language)}</Text>
                  </Row>
                  {discountAmount > 0 && (
                    <Row justify="space-between" style={{ marginBottom: 2 }}>
                      <Text style={{ color: '#333', fontSize: '11px' }}>{t('discount')}:</Text>
                      <Text style={{ color: '#d32f2f', fontWeight: 'bold', fontSize: '11px' }}>- ฿{formatNumber(discountAmount, i18n.language)}</Text>
                    </Row>
                  )}
                  <Row justify="space-between" style={{ marginBottom: 2 }}>
                    <Text style={{ color: '#333', fontSize: '11px' }}>{t('Total After Discount')}:</Text>
                    <Text style={{ color: '#333', fontWeight: '500', fontSize: '11px' }}>฿{formatNumber(totalAfterDiscount, i18n.language)}</Text>
                  </Row>
                  {vatAmount > 0 && (
                    <Row justify="space-between" style={{ marginBottom: 4 }}>
                      <Text style={{ color: '#333', fontSize: '11px' }}>{t('VAT 7%', { percentage: 7 })}:</Text>
                      <Text style={{ color: '#333', fontWeight: '500', fontSize: '11px' }}>฿{formatNumber(vatAmount, i18n.language)}</Text>
                    </Row>
                  )}
                  <Divider style={{ 
                    margin: '4px 0',
                    borderColor: '#6c757d',
                    borderWidth: '1px'
                  }} />
                  <Row justify="space-between" style={{
                    backgroundColor: '#6c757d',
                    margin: '-8px -8px 4px -8px',
                    padding: '6px 8px',
                    borderRadius: '4px'
                  }}>
                    <Text strong style={{ fontSize: 13, color: 'white' }}>
                      {t('grandTotal')}:
                    </Text>
                    <Text strong style={{ fontSize: 13, color: 'white' }}>
                      ฿{formatNumber(grandTotal, i18n.language)}
                    </Text>
                  </Row>
                  {remainingBalance > 0 && (
                    <Row justify="space-between" style={{ marginTop: 4 }}>
                      <Text style={{ color: '#d32f2f', fontWeight: 'bold', fontSize: '11px' }}>{t('Remaining Balance')}:</Text>
                      <Text style={{ color: '#d32f2f', fontWeight: 'bold', fontSize: '11px' }}>
                        (฿{formatNumber(remainingBalance, i18n.language)})
                      </Text>
                    </Row>
                  )}
                </div>
              </Col>
            </Row>
            <Divider style={{ margin: '20px 0', borderColor: '#ccc' }} />
                <div style={{ display: 'flex', gap: '16px', marginTop: 8, alignItems: 'flex-start' }}>
                {/* Payment Information - Compact */}
                <div style={{ flex: 1, marginTop: 16 }}>
                  <Title level={5} style={{ marginBottom: 6, color: '#333', fontSize: '13px' }}>
                    {t('Payment Information')}
                  </Title>
                  <Card 
                    size="small" 
                    className="payment-card"
                    style={{
                      backgroundColor: '#f9f9f9',
                      border: '1px solid #ccc',
                      borderRadius: '6px'
                    }}
                    bodyStyle={{ padding: '8px 10px' }}
                  >
                    <div className="bank-info">
                      <div className="bank-details">
                        <Text style={{ color: '#333', fontWeight: '500', fontSize: '10px', display: 'block' }}>{bankInfo.bankName}</Text>
                        <Text style={{ color: '#333', fontSize: '10px', display: 'block' }}>{bankInfo.accountName}</Text>
                        <Text style={{ color: '#333', fontWeight: 'bold', fontSize: '10px', display: 'block' }}>เลขที่บัญชี {bankInfo.accountNumber}</Text>
                        {bankInfo.branchName && (
                          <Text style={{ color: '#333', fontSize: '10px', display: 'block' }}>สาขา {bankInfo.branchName}</Text>
                        )}
                        {bankInfo.swiftCode && (
                          <Text style={{ color: '#333', fontSize: '10px', display: 'block' }}>SWIFT: {bankInfo.swiftCode}</Text>
                        )}
                      </div>
                    </div>
                  </Card>
                </div>
                
                
                {/* Terms and Conditions - Compact */}
                <div className="terms-section" style={{ flex: 1, marginTop: 16 }}>
                  <Title level={5} style={{ marginBottom: 6, color: '#333', fontSize: '13px' }}>
                    Terms and Conditions
                  </Title>
                  <Card 
                    size="small" 
                    style={{ 
                      marginBottom: 16,
                      backgroundColor: '#f9f9f9',
                      border: '1px solid #ccc',
                      borderRadius: '6px'
                    }}
                    bodyStyle={{ padding: '8px 10px' }}
                  >
                    <div style={{ fontSize: 9, lineHeight: 1.4, color: '#333' }}>
                      <div style={{ marginBottom: 2 }}>
                        This invoice is valid until <strong style={{ color: '#333' }}>{formattedExpirationDate}</strong>. Kindly make the full tuition payment via bank transfer no later than <strong style={{ color: '#333' }}>{formattedExpirationDate}</strong>.
                      </div>
                      <div style={{ marginBottom: 2 }}>
                        All payment cannot be refunded.
                      </div>
                    </div>
                  </Card>
                </div>
              </div>
               {/* Signature Section - Compact */}
              <Row justify="center" align="bottom" style={{ marginBottom: 16 }}>
                <Col span={12}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    {/* Authorized Signature */}
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ height: 60 }} />
                      <div style={{
                        borderTop: '1px solid #6c757d',
                        width: '140px',
                        margin: '0 auto',
                        paddingTop: 3,
                        fontSize: 11,
                        color: '#333'
                      }}>
                        <Text style={{ color: '#333', fontWeight: '500', fontSize: '11px' }}>{t('Authorized Signature')}</Text>
                      </div>
                    </div>

                    {/* Company Seal */}
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ height: 60 }} />
                      <div style={{
                        borderTop: '1px solid #6c757d',
                        width: '140px',
                        margin: '0 auto',
                        paddingTop: 3,
                        fontSize: 11,
                        color: '#333'
                      }}>
                        <Text style={{ color: '#333', fontWeight: '500', fontSize: '11px' }}>{t('Company Seal')}</Text>
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>
          
            {/* Print & Download Buttons - Compact */}
            <div className="invoice-actions" style={{ textAlign: 'center', marginTop: 12, display: 'flex', justifyContent: 'center', gap: 12 }}>
              <button
                onClick={handlePrint}
                className="print-button"
                style={{
                  backgroundColor: '#6c757d',
                  border: '1px solid #495057',
                  color: 'white',
                  padding: '8px 20px',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                }}
                onMouseOver={(e) => {
                  e.target.style.backgroundColor = '#495057';
                  e.target.style.transform = 'translateY(-1px)';
                }}
                onMouseOut={(e) => {
                  e.target.style.backgroundColor = '#6c757d';
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                {t('printInvoice')}
              </button>
              <button
                onClick={handleDownloadPDF}
                style={{
                  backgroundColor: '#1890ff',
                  border: '1px solid #096dd9',
                  color: 'white',
                  padding: '8px 20px',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                }}
                onMouseOver={(e) => {
                  e.target.style.backgroundColor = '#096dd9';
                  e.target.style.transform = 'translateY(-1px)';
                }}
                onMouseOut={(e) => {
                  e.target.style.backgroundColor = '#1890ff';
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                {t('Download PDF')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default InvoiceModal;